import React from 'react';
import { Layer } from 'react-mapbox-gl';
import PropTypes from 'prop-types';

import createMapImage from '../../utils/createMapImage';
import bumpIconPark from '../../img/bump_icon_park.png';
import bumpIconOvernight from '../../img/bump_icon_overnight.png';

import markerIconAttraction from '../../img/marker_icon_attractie.png';
import markerIconShow from '../../img/marker_icon_show.png';
import markerIconTheater from '../../img/marker_icon_theater.png';
import markerIconRestaurant from '../../img/marker_icon_restaurant.png';
import markerIconFairytale from '../../img/marker_icon_fairytale.png';
import markerIconMerchandise from '../../img/marker_icon_merchandise.png';
import markerIconDomain from '../../img/marker_icon_verblijf.png';

import markerActiveAttraction from '../../img/marker_icon_attractie-active.png';
import markerActiveShow from '../../img/marker_icon_show-active.png';
import markerActiveTheater from '../../img/marker_icon_theater-active.png';
import markerActiveRestaurant from '../../img/marker_icon_restaurant-active.png';
import markerActiveFairytale from '../../img/marker_icon_fairytale-active.png';
import markerActiveMerchandise from '../../img/marker_icon_merchandise-active.png';
import markerActiveDomain from '../../img/marker_icon_verblijf-active.png';

const bumpLayerImages = [
    ['bump_icon_park', createMapImage(bumpIconPark), { pixelRatio: 2.5 }],
    ['bump_icon_overnight', createMapImage(bumpIconOvernight), { pixelRatio: 2.5 }],
];
export const AreasLayer = ({ zoomThreshold, children, images = bumpLayerImages }) => (
    <Layer
        type="symbol"
        id="bumps-layer"
        layout={{
            'icon-allow-overlap': true,
            'icon-image': ['concat', 'bump_icon_', ['get', 'CATEGORY']],
            'icon-size': 2,
            'text-allow-overlap': true,
            'text-field': '{NAME}',
            'text-max-width': 13, // ems
            'text-offset': [0, 1],
            'text-font': ['Alegreya Bold'],
            'text-anchor': 'center',
            'text-size': 16,
            'text-line-height': 1,
        }}
        paint={{
            'text-color': '#fff',
        }}
        maxZoom={zoomThreshold}
        images={images}
    >
        {children}
    </Layer>
);

AreasLayer.propTypes = {
    zoomThreshold: PropTypes.number.isRequired,
    images: PropTypes.array,
};

export const DotsLayer = ({ zoomThreshold, children }) => (
    <Layer
        type="circle"
        id="circle-layer"
        paint={{
            'circle-radius': 4,
            'circle-color': '#4D3929',
            'circle-stroke-width': 3,
            'circle-stroke-color': '#fff',
            'circle-opacity': 1,
            'circle-stroke-opacity': 1,
        }}
        minZoom={zoomThreshold}
    >
        {children}
    </Layer>
);

DotsLayer.propTypes = {
    zoomThreshold: PropTypes.number.isRequired,
};

const pinLayerImages = [
    ['marker_icon_attraction', createMapImage(markerIconAttraction), { pixelRatio: 2 }],
    ['marker_icon_fairytale', createMapImage(markerIconFairytale), { pixelRatio: 2 }],
    ['marker_icon_show', createMapImage(markerIconShow), { pixelRatio: 2 }],
    ['marker_icon_theater', createMapImage(markerIconTheater), { pixelRatio: 2 }],
    ['marker_icon_restaurant', createMapImage(markerIconRestaurant), { pixelRatio: 2 }],
    ['marker_icon_merchandise', createMapImage(markerIconMerchandise), { pixelRatio: 2 }],
    ['marker_icon_domain', createMapImage(markerIconDomain), { pixelRatio: 2 }],
];

export const PinsLayer = ({ zoomThreshold, children, images = pinLayerImages }) => (
    <Layer
        type="symbol"
        id="pin-layer"
        layout={{
            'icon-image': ['concat', 'marker_icon_', ['get', 'CATEGORY']],
            'icon-allow-overlap': true,
            'icon-padding': 0,
            'icon-anchor': 'bottom',
            'icon-offset': [0, 10],
        }}
        paint={{
            'icon-opacity': 0,
            'icon-opacity-transition': { duration: 1000 },
        }}
        minZoom={zoomThreshold}
        images={images}
    >
        {children}
    </Layer>
);

PinsLayer.propTypes = {
    zoomThreshold: PropTypes.number.isRequired,
    images: PropTypes.array,
};

const domainLayerImages = [['marker_icon_domain', createMapImage(markerIconDomain), { pixelRatio: 2 }]];

export const DomainLayer = ({ zoomThreshold, children, images = domainLayerImages }) => (
    <Layer
        type="symbol"
        id="domain-layer"
        layout={{
            'icon-image': 'marker_icon_domain',
            'icon-allow-overlap': true,
            'icon-padding': 0,
            'icon-anchor': 'bottom',
            'icon-offset': [0, 10],
        }}
        minZoom={zoomThreshold}
        images={images}
    >
        {children}
    </Layer>
);

DomainLayer.propTypes = {
    zoomThreshold: PropTypes.number.isRequired,
    images: PropTypes.array,
};

const activeLayerImages = [
    ['marker_active_attraction', createMapImage(markerActiveAttraction), { pixelRatio: 2 }],
    ['marker_active_fairytale', createMapImage(markerActiveFairytale), { pixelRatio: 2 }],
    ['marker_active_show', createMapImage(markerActiveShow), { pixelRatio: 2 }],
    ['marker_active_theater', createMapImage(markerActiveTheater), { pixelRatio: 2 }],
    ['marker_active_restaurant', createMapImage(markerActiveRestaurant), { pixelRatio: 2 }],
    ['marker_active_merchandise', createMapImage(markerActiveMerchandise), { pixelRatio: 2 }],
    ['marker_active_domain', createMapImage(markerActiveDomain), { pixelRatio: 2 }],
];

export const ActivePinLayer = ({ zoomThreshold, children, images = activeLayerImages, poiId = false }) => (
    <Layer
        type="symbol"
        id="active-layer"
        layout={{
            'icon-image': ['concat', 'marker_active_', ['get', 'CATEGORY']],
            'icon-allow-overlap': true,
            'icon-padding': 0,
            'icon-anchor': 'bottom',
            'icon-offset': [0, 10.5],
            'icon-size': 0,
        }}
        paint={{
            'icon-opacity': 0,
            'icon-opacity-transition': { duration: 600 },
        }}
        minZoom={zoomThreshold}
        images={images}
        filter={['==', 'POI_ID', poiId]}
    >
        {children}
    </Layer>
);

ActivePinLayer.propTypes = {
    zoomThreshold: PropTypes.number.isRequired,
    images: PropTypes.array,
    poiId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};
